import React, { useState, useMemo, useRef, useEffect } from 'react';
import { motion } from 'motion/react';
import { useConfirm } from '../common/ConfirmDialog';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Divider,
  Chip,
  CircularProgress,
  Alert,
  Switch,
  FormControlLabel,
  Paper,
  Tooltip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormHelperText,
  Avatar,
  Checkbox,
  InputAdornment,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Close as CloseIcon,
  Apps as AppsIcon,
  ExpandMore as ExpandMoreIcon,
  Group as GroupIcon,
  Groups as GroupsIcon,
  PersonAdd as PersonAddIcon,
  GroupAdd as GroupAddIcon,
  Security as SecurityIcon,
  Search as SearchIcon,
  ArrowBack as ArrowBackIcon,
  ChevronRight as ChevronRightIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { getCustomerIdFromSubdomain } from '../../utils/constants';
import {
  getMapperTypesForProtocol,
  getProtocolMapper,
  getMapperTypeHelperText,
  getMapperFields,
  getMapperFieldDefaults,
  isOidcProtocol,
  normalizeProtocol,
} from '../../utils/mapperFields';
import MapperDetails from './MapperDetails';
import OidcConnectionDetails from './OidcConnectionDetails';
import SamlConnectionPanel from './SamlConnectionPanel';
import apiService from '../../services/api';
import { pageVariants, sectionVariants } from '../../animations/variants';
import { useReadOnlyMode } from '../../hooks/useReadOnlyMode';
import { useDirectorySearch } from '../../hooks/useDirectorySearch';
import { ReadOnlyButton, ReadOnlyIconButton } from '../common/ReadOnlyButton';

/*
 * Both of these cause real support tickets, so the catalogue's helper text is promoted out
 * of small grey FormHelperText into a warning the admin has to look past. The text is not
 * duplicated here — the two renderings are mutually exclusive.
 */
const OIDC_WARNING_TYPES = new Set([
  'oidc-usermodel-property-mapper',
  'oidc-usermodel-client-role-mapper',
]);

// The dialog opens on, and resets to, the first mapper type Prism offers for the protocol.
// `protocol` is normalised rather than passed through: an app carrying the 'oidc' alias
// must not put that spelling on a mapper Keycloak then has to interpret.
//
// `pins` carries the values the OWNING APPLICATION supplies for catalogue fields the
// backend refuses to take from the admin — see getMapperFieldDefaults.
const buildInitialMapperData = (protocol, pins) => {
  const [first] = getMapperTypesForProtocol(protocol);
  return {
    name: '',
    protocol: normalizeProtocol(protocol),
    protocolMapper: first.protocolMapper,
    mapperType: first.value,
    attributeName: '',
    userAttribute: '',
    friendlyName: '',
    groupAttribute: 'member',
    fullGroupPath: true,
    singleGroupAttribute: false,
    oidcConfig: isOidcProtocol(protocol) ? getMapperFieldDefaults(first.value, protocol, pins) : {},
  };
};

const inputStyle = {
  '& .MuiOutlinedInput-root': {
    fontFamily: 'Metropolis, sans-serif',
    backgroundColor: '#FFFFFF',
    '& fieldset': { borderColor: '#E5E5E5' },
    '&:hover fieldset': { borderColor: '#27AE60' },
    '&.Mui-focused fieldset': { borderColor: '#27AE60' },
  },
  '& .MuiInputLabel-root': {
    fontFamily: 'Metropolis, sans-serif',
    color: '#666666',
    '&.Mui-focused': { color: '#27AE60' },
  },
  '& .MuiFormHelperText-root': {
    fontFamily: 'Metropolis, sans-serif',
  },
};

const selectStyle = {
  '& .MuiOutlinedInput-root': {
    fontFamily: 'Metropolis, sans-serif',
    backgroundColor: '#FFFFFF',
    '& fieldset': { borderColor: '#E5E5E5' },
    '&:hover fieldset': { borderColor: '#27AE60' },
    '&.Mui-focused fieldset': { borderColor: '#27AE60' },
  },
  '& .MuiInputLabel-root': {
    fontFamily: 'Metropolis, sans-serif',
    color: '#666666',
    '&.Mui-focused': { color: '#27AE60' },
  },
};

const dialogPaperProps = {
  sx: {
    borderRadius: '12px',
    border: '1px solid #E5E5E5',
    backgroundColor: '#FFFFFF',
  },
};

const userAttributes = [
  { value: 'username', label: 'Username' },
  { value: 'email', label: 'Email' },
  { value: 'firstName', label: 'First Name' },
  { value: 'lastName', label: 'Last Name' },
  { value: 'locale', label: 'Locale' },
];

/**
 * One input for an OIDC mapper config key, rendered entirely off its catalogue entry:
 * `kind` picks the control, `options` fills a select, `required` marks a text field.
 *
 * Nothing here knows which mapper type it belongs to. The catalogue already encodes that —
 * `user.attribute` is a closed list on the property mapper and free text on the attribute
 * mapper because the two entries declare different kinds, not because of a branch here.
 *
 * A `readOnly` field is rendered prefilled and non-editable rather than hidden: the value
 * still goes on the wire (Keycloak expects the key), and showing it is the only way the
 * admin learns what the mapper is actually pinned to.
 *
 * @param {object} field - one entry from getMapperFields():
 *   {key, label, kind, options?, required?, readOnly?, helperText?}
 */
const OidcMapperField = ({ field, value, onChange }) => {
  const { key, label, kind, options, required, readOnly, helperText } = field;

  if (kind === 'boolean') {
    return (
      <FormControlLabel
        control={
          <Switch
            checked={Boolean(value)}
            onChange={(e) => onChange(key, e.target.checked)}
            sx={{ '& .Mui-checked': { color: '#27AE60' } }}
          />
        }
        label={<Typography sx={{ fontFamily: 'Metropolis, sans-serif', fontSize: '0.875rem', color: '#666666' }}>{label}</Typography>}
      />
    );
  }

  if (kind === 'select') {
    return (
      <FormControl fullWidth size="small" sx={selectStyle}>
        <InputLabel>{label}</InputLabel>
        <Select value={value} onChange={(e) => onChange(key, e.target.value)} label={label}>
          {options.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
        </Select>
      </FormControl>
    );
  }

  return (
    <TextField
      label={label}
      value={value}
      onChange={(e) => onChange(key, e.target.value)}
      required={Boolean(required)}
      fullWidth
      size="small"
      helperText={helperText}
      // readOnly rather than disabled: the value is still submitted, still selectable and
      // still reads at full contrast, because it is information the admin needs, not an
      // input that happens to be unavailable right now.
      InputProps={{ readOnly: Boolean(readOnly) }}
      sx={inputStyle}
    />
  );
};

const CustomApplicationDetailView = ({ app, onBack, onEdit, onDelete }) => {
  const confirm = useConfirm();
  const isReadOnly = useReadOnlyMode();
  const customerId = getCustomerIdFromSubdomain();

  // An application response that predates OIDC support carries no protocol at all; the
  // mapperFields helpers treat anything unrecognised as SAML, which is the old behaviour.
  // Both of these go through the same normalisation — deriving one with a raw `===` split
  // the view in half on the 'oidc' alias: OIDC mappers offered, but no OIDC inputs shown
  // and a submit that fell through to the SAML branch and posted an empty config.
  const isOidc = isOidcProtocol(app.protocol);
  const mapperTypes = getMapperTypesForProtocol(app.protocol);

  // The values the backend pins to this application rather than accepting from the admin.
  // Currently just the client-role mapper's clientId; see mapperFields.js.
  const mapperPins = { clientId: app.clientId };

  // Mapper state
  const [showMapperDialog, setShowMapperDialog] = useState(false);
  const [mapperData, setMapperData] = useState(() => buildInitialMapperData(app.protocol, { clientId: app.clientId }));
  const [mappers, setMappers] = useState(null);
  const [loadingMappers, setLoadingMappers] = useState(false);

  // Metadata state
  const [downloadingMetadata, setDownloadingMetadata] = useState(false);

  /*
   * OIDC realm endpoint state.
   *
   * The list response deliberately carries no oidcEndpoints — the set is identical for
   * every application in the tenant, so the backend sends it once, on the single GET
   * (getCustomApplicationHandler) and nulls it on every list row. This view is rendered
   * from list state, so without re-reading the single record here the connection panel's
   * seven endpoint rows show '--' for every real customer.
   *
   * Seeded from the prop so a caller that already holds a full record renders immediately
   * rather than flashing a skeleton at a value it already has.
   */
  const [oidcEndpoints, setOidcEndpoints] = useState(app.oidcEndpoints || null);
  const [loadingEndpoints, setLoadingEndpoints] = useState(false);
  const [endpointsError, setEndpointsError] = useState(null);

  // Role state
  const [showRoleDialog, setShowRoleDialog] = useState(false);
  const [showAssignRoleDialog, setShowAssignRoleDialog] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);
  const [roleData, setRoleData] = useState({ roleName: '', description: '' });
  const [roles, setRoles] = useState(null);
  const [loadingRoles, setLoadingRoles] = useState(false);
  const [roleUsers, setRoleUsers] = useState({});
  const [availableUsers, setAvailableUsers] = useState([]);
  const [selectedUserForRole, setSelectedUserForRole] = useState('');
  const [showAssignRoleToGroupDialog, setShowAssignRoleToGroupDialog] = useState(false);
  const [roleGroups, setRoleGroups] = useState({});
  const [selectedGroupForRole, setSelectedGroupForRole] = useState('');

  // Assign users state
  const [showAssignUsersDialog, setShowAssignUsersDialog] = useState(false);
  const [applicationUsers, setApplicationUsers] = useState(null);
  const [loadingApplicationUsers, setLoadingApplicationUsers] = useState(false);
  const [selectedUsersForAssignment, setSelectedUsersForAssignment] = useState([]);
  const [userAssignmentSearch, setUserAssignmentSearch] = useState('');

  // Assign groups state
  const [showAssignGroupsDialog, setShowAssignGroupsDialog] = useState(false);
  const [applicationGroups, setApplicationGroups] = useState(null);
  const [loadingApplicationGroups, setLoadingApplicationGroups] = useState(false);
  const [selectedGroupsForAssignment, setSelectedGroupsForAssignment] = useState([]);
  const [availableGroups, setAvailableGroups] = useState([]);
  const [groupAssignmentSearch, setGroupAssignmentSearch] = useState('');

  // Typing searches Keycloak so the whole realm is reachable, not just the page
  // loaded when the dialog opened. Already-assigned principals stay excluded
  // client-side — that set is bounded and already in hand.
  const userDirectory = useDirectorySearch('users', userAssignmentSearch);
  const groupDirectory = useDirectorySearch('groups', groupAssignmentSearch);

  // Filtered available users
  const filteredAvailableUsers = useMemo(() => {
    const assigned = applicationUsers || [];
    const source = userAssignmentSearch.trim() ? userDirectory.results : availableUsers;
    return source.filter(u => !assigned.some(a => a.username === u.username));
  }, [availableUsers, applicationUsers, userAssignmentSearch, userDirectory.results]);

  // Filtered available groups
  const filteredAvailableGroups = useMemo(() => {
    const assigned = applicationGroups || [];
    const source = groupAssignmentSearch.trim() ? groupDirectory.results : availableGroups;
    return source.filter(g => !assigned.some(a => a.groupName === g.name));
  }, [availableGroups, applicationGroups, groupAssignmentSearch, groupDirectory.results]);

  // Select all / indeterminate for users
  const isAllUsersSelected = filteredAvailableUsers.length > 0 &&
    filteredAvailableUsers.every(u => selectedUsersForAssignment.includes(u.username));
  const isUsersIndeterminate = selectedUsersForAssignment.length > 0 &&
    filteredAvailableUsers.some(u => selectedUsersForAssignment.includes(u.username)) &&
    !isAllUsersSelected;

  // Select all / indeterminate for groups
  const isAllGroupsSelected = filteredAvailableGroups.length > 0 &&
    filteredAvailableGroups.every(g => selectedGroupsForAssignment.includes(g.name));
  const isGroupsIndeterminate = selectedGroupsForAssignment.length > 0 &&
    filteredAvailableGroups.some(g => selectedGroupsForAssignment.includes(g.name)) &&
    !isAllGroupsSelected;

  const handleSelectAllUsers = () => {
    const allUsernames = filteredAvailableUsers.map(u => u.username);
    if (isAllUsersSelected) {
      setSelectedUsersForAssignment(prev => prev.filter(u => !allUsernames.includes(u)));
    } else {
      setSelectedUsersForAssignment(prev => [...new Set([...prev, ...allUsernames])]);
    }
  };

  const handleToggleUserAssignment = (username) => {
    setSelectedUsersForAssignment(prev =>
      prev.includes(username) ? prev.filter(u => u !== username) : [...prev, username]
    );
  };

  const handleSelectAllGroups = () => {
    const allGroupNames = filteredAvailableGroups.map(g => g.name);
    if (isAllGroupsSelected) {
      setSelectedGroupsForAssignment(prev => prev.filter(g => !allGroupNames.includes(g)));
    } else {
      setSelectedGroupsForAssignment(prev => [...new Set([...prev, ...allGroupNames])]);
    }
  };

  const handleToggleGroupAssignment = (groupName) => {
    setSelectedGroupsForAssignment(prev =>
      prev.includes(groupName) ? prev.filter(g => g !== groupName) : [...prev, groupName]
    );
  };

  /*
   * One copiedKey for the whole view, per CopyRow's contract: several rows share one piece
   * of state and only the row actually clicked shows the tick. The reset compares before
   * clearing so a second copy does not have its tick cancelled by the first row's timer.
   */
  const [copiedKey, setCopiedKey] = useState(null);
  const copyResetRef = useRef(null);

  useEffect(() => () => clearTimeout(copyResetRef.current), []);

  const handleCopy = async (key, value) => {
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      setCopiedKey(key);
      clearTimeout(copyResetRef.current);
      copyResetRef.current = setTimeout(
        () => setCopiedKey((current) => (current === key ? null : current)),
        2000
      );
      toast.success('Copied to clipboard');
    } catch (err) {
      toast.error('Failed to copy to clipboard');
    }
  };

  /*
   * --- OIDC realm endpoints ---
   *
   * Re-read the single record so the connection panel has endpoints to show. Only when
   * they are actually missing: a record that already carries them needs no round trip.
   *
   * Deliberately NOT solved by putting OIDCEndpoints on the list rows. That would send the
   * same seven URLs on every row of every list response, and contradicts the documented
   * list/get split the backend's own comments spell out.
   *
   * Failure is non-fatal by construction: only the realm section depends on this state, so
   * the client ID, secret controls and redirect URIs above it render either way.
   */
  useEffect(() => {
    if (!isOidc) return undefined;
    if (app.oidcEndpoints) {
      setOidcEndpoints(app.oidcEndpoints);
      setEndpointsError(null);
      // Also clears the flag when a fetch was still in flight and the owner supplied the
      // endpoints from elsewhere first: its cleanup abandons the response, so nothing else
      // would ever turn the skeletons off.
      setLoadingEndpoints(false);
      return undefined;
    }

    let cancelled = false;
    setOidcEndpoints(null);
    setEndpointsError(null);
    setLoadingEndpoints(true);

    (async () => {
      try {
        const response = await apiService.getCustomApplication(customerId, app.id);
        if (cancelled) return;
        const endpoints = response?.data?.oidcEndpoints;
        if (endpoints) {
          setOidcEndpoints(endpoints);
        } else {
          setEndpointsError(response?.error || 'The server did not return the realm endpoints.');
        }
      } catch (error) {
        if (cancelled) return;
        console.error('Failed to fetch OIDC realm endpoints:', error);
        setEndpointsError(error.message || 'Failed to load the realm endpoints.');
      } finally {
        if (!cancelled) setLoadingEndpoints(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isOidc, customerId, app.id, app.oidcEndpoints]);

  // --- Mappers ---
  const fetchMappers = async () => {
    setLoadingMappers(true);
    try {
      const response = await apiService.getCustomApplicationMappers(customerId, app.id);
      if (response.success) {
        setMappers(response.data.mappers || []);
      }
    } catch (error) {
      console.error('Failed to fetch mappers:', error);
    } finally {
      setLoadingMappers(false);
    }
  };

  const handleMapperTypeChange = (e) => {
    const mapperType = e.target.value;
    setMapperData({
      ...mapperData,
      mapperType,
      protocolMapper: getProtocolMapper(mapperType, app.protocol),
      // Each OIDC type carries its own token-placement defaults, so they are re-seeded
      // rather than carried across from the type the admin just left. Pinned keys come
      // back prefilled from the application on every re-seed, not just the first.
      oidcConfig: isOidc ? getMapperFieldDefaults(mapperType, app.protocol, mapperPins) : {},
    });
  };

  const setOidcField = (key, value) =>
    setMapperData((prev) => ({ ...prev, oidcConfig: { ...prev.oidcConfig, [key]: value } }));

  // The fields the selected OIDC type declares, in dialog order. Empty for SAML, whose
  // dialog is a hand-written branch per type rather than a catalogue-driven form.
  const oidcFields = isOidc ? getMapperFields(mapperData.mapperType, app.protocol) : [];

  const openMapperDialog = () => {
    setMapperData(buildInitialMapperData(app.protocol, mapperPins));
    setShowMapperDialog(true);
  };

  const handleMapperSubmit = async (e) => {
    e.preventDefault();
    let mapperPayload = {
      name: mapperData.name,
      protocol: mapperData.protocol,
      protocolMapper: mapperData.protocolMapper,
      config: {},
    };

    if (isOidc) {
      // Driven straight off the catalogue: only the keys the selected type declares are
      // sent, so an OIDC mapper never carries config from a type the admin passed through.
      // MUI's Select does not take part in the form's native validation, so a required
      // field is checked here rather than left to the browser.
      const missing = oidcFields.find(
        ({ key, required }) => required && !String(mapperData.oidcConfig[key] ?? '').trim()
      );
      if (missing) {
        toast.error(`${missing.label} is required`);
        return;
      }
      // Keycloak stores every mapper config value as a string.
      oidcFields.forEach(({ key, kind }) => {
        const value = mapperData.oidcConfig[key];
        mapperPayload.config[key] = kind === 'boolean' ? String(Boolean(value)) : value;
      });
      return submitMapper(mapperPayload);
    }

    switch(mapperData.mapperType) {
      case 'user-attribute':
        mapperPayload.config = {
          'attribute.name': mapperData.attributeName,
          'attribute.nameformat': 'Basic',
          'user.attribute': mapperData.userAttribute,
          'friendly.name': mapperData.friendlyName || mapperData.attributeName,
        };
        break;
      case 'role-list':
        mapperPayload.config = {
          'attribute.name': mapperData.attributeName || 'Role',
          'attribute.nameformat': 'Basic',
          'single': 'false',
          'friendly.name': mapperData.friendlyName || 'Role',
        };
        break;
      case 'group-list':
        mapperPayload.config = {
          'attribute.name': mapperData.attributeName || 'Group',
          'attribute.nameformat': 'Basic',
          'single': mapperData.singleGroupAttribute ? 'true' : 'false',
          'full.path': mapperData.fullGroupPath ? 'true' : 'false',
          'friendly.name': mapperData.friendlyName || 'Group',
        };
        break;
    }

    return submitMapper(mapperPayload);
  };

  // Shared tail of both protocol branches: only the config above differs.
  const submitMapper = async (mapperPayload) => {
    try {
      const response = await apiService.createCustomApplicationMapper(customerId, app.id, mapperPayload);
      if (response.success) {
        toast.success('Mapper created successfully');
        setShowMapperDialog(false);
        setMapperData(buildInitialMapperData(app.protocol, mapperPins));
        fetchMappers();
      } else {
        toast.error(response.error || 'Failed to create mapper');
      }
    } catch (error) {
      console.error('Mapper creation error:', error);
      // The backend's own text, not a generic stand-in: mapper rejections are the precise
      // explanations — which types are allowlisted, which claim prefixes are reserved,
      // which client the mapper is pinned to — and none of that survives 'Failed to
      // create mapper'.
      toast.error(error.message || 'Failed to create mapper');
    }
  };

  const handleDeleteMapper = async (mapper) => {
    const confirmed = await confirm({
      title: 'Delete Mapper',
      message: `Are you sure you want to delete the mapper "${mapper.name}"? This action cannot be undone.`,
      confirmText: 'Delete',
      confirmColor: 'error',
    });
    if (!confirmed) return;

    try {
      const response = await apiService.deleteCustomApplicationMapper(customerId, app.id, mapper.id);
      if (response.success) {
        toast.success('Mapper deleted successfully');
        fetchMappers();
      } else {
        toast.error(response.error || 'Failed to delete mapper');
      }
    } catch (error) {
      console.error('Mapper deletion error:', error);
      toast.error('Failed to delete mapper');
    }
  };

  // --- Metadata ---
  const handleDownloadMetadata = async () => {
    setDownloadingMetadata(true);
    try {
      const metadataXML = await apiService.getCustomApplicationMetadata(customerId, app.id);
      const blob = new Blob([metadataXML], { type: 'application/xml' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${app.clientId}-metadata.xml`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('Metadata downloaded successfully');
    } catch (error) {
      console.error('Failed to download metadata:', error);
      toast.error('Failed to download metadata');
    } finally {
      setDownloadingMetadata(false);
    }
  };

  // --- Roles ---
  const fetchRoles = async () => {
    setLoadingRoles(true);
    try {
      const response = await apiService.listCustomApplicationRoles(customerId, app.id);
      if (response.success) {
        setRoles(response.data.roles || []);
      }
    } catch (error) {
      console.error('Failed to fetch roles:', error);
    } finally {
      setLoadingRoles(false);
    }
  };

  const handleCreateRole = () => {
    setShowRoleDialog(true);
  };

  const handleRoleSubmit = async (e) => {
    e.preventDefault();
    if (!roleData.roleName) {
      toast.error('Role name is required');
      return;
    }
    try {
      const response = await apiService.createCustomApplicationRole(customerId, app.id, roleData);
      if (response.success) {
        toast.success('Role created successfully');
        setShowRoleDialog(false);
        setRoleData({ roleName: '', description: '' });
        fetchRoles();
      } else {
        toast.error(response.error || 'Failed to create role');
      }
    } catch (error) {
      console.error('Role creation error:', error);
      toast.error('Failed to create role');
    }
  };

  const handleDeleteRole = async (roleName) => {
    const confirmed = await confirm({
      title: 'Delete Role',
      message: `Delete role "${roleName}"?`,
      confirmText: 'Delete',
      variant: 'danger',
    });
    if (!confirmed) return;
    try {
      const response = await apiService.deleteCustomApplicationRole(customerId, app.id, roleName);
      if (response.success) {
        toast.success('Role deleted successfully');
        fetchRoles();
      } else {
        toast.error(response.error || 'Failed to delete role');
      }
    } catch (error) {
      console.error('Role deletion error:', error);
      toast.error('Failed to delete role');
    }
  };

  const fetchRoleUsers = async (roleName) => {
    try {
      const response = await apiService.listRoleUsers(customerId, app.id, roleName);
      if (response.success) {
        setRoleUsers(prev => ({ ...prev, [roleName]: response.data.users || [] }));
      }
    } catch (error) {
      console.error('Failed to fetch role users:', error);
    }
  };

  const handleAssignRole = async (role) => {
    setSelectedRole(role);
    try {
      const response = await apiService.listApplicationUsers(customerId, app.id);
      if (response.success && response.data) {
        const assignedUsers = (response.data.users || []).map(a => ({ username: a.username, email: a.username }));
        setAvailableUsers(assignedUsers);
      }
    } catch (error) {
      console.error('Failed to fetch application users:', error);
    }
    await fetchRoleUsers(role.name);
    setShowAssignRoleDialog(true);
  };

  const handleAssignRoleToUser = async () => {
    if (!selectedUserForRole) {
      toast.error('Please select a user');
      return;
    }
    try {
      const response = await apiService.assignRoleToUser(customerId, app.id, selectedRole.name, selectedUserForRole);
      if (response.success) {
        toast.success('Role assigned to user successfully');
        setSelectedUserForRole('');
        await fetchRoleUsers(selectedRole.name);
      } else {
        toast.error(response.error || 'Failed to assign role');
      }
    } catch (error) {
      console.error('Role assignment error:', error);
      toast.error('Failed to assign role');
    }
  };

  const handleRemoveRoleFromUser = async (username) => {
    const confirmed = await confirm({
      title: 'Remove Role',
      message: `Remove role from "${username}"?`,
      confirmText: 'Remove',
      variant: 'warning',
    });
    if (!confirmed) return;
    try {
      const response = await apiService.removeRoleFromUser(customerId, app.id, selectedRole.name, username);
      if (response.success) {
        toast.success('Role removed from user successfully');
        await fetchRoleUsers(selectedRole.name);
      } else {
        toast.error(response.error || 'Failed to remove role');
      }
    } catch (error) {
      console.error('Role removal error:', error);
      toast.error('Failed to remove role');
    }
  };

  const fetchRoleGroups = async (roleName) => {
    try {
      const response = await apiService.listRoleGroups(customerId, app.id, roleName);
      if (response.success) {
        setRoleGroups(prev => ({ ...prev, [roleName]: response.data.groups || [] }));
      }
    } catch (error) {
      console.error('Failed to fetch role groups:', error);
    }
  };

  const handleAssignRoleToGroup = async (role) => {
    setSelectedRole(role);
    try {
      const response = await apiService.listApplicationGroups(customerId, app.id);
      if (response.success && response.data) {
        const assignedGroups = (response.data.groups || []).map(a => ({ name: a.groupName }));
        setAvailableGroups(assignedGroups);
      }
    } catch (error) {
      console.error('Failed to fetch application groups:', error);
    }
    await fetchRoleGroups(role.name);
    setShowAssignRoleToGroupDialog(true);
  };

  const handleAssignRoleToGroupSubmit = async () => {
    if (!selectedGroupForRole) {
      toast.error('Please select a group');
      return;
    }
    try {
      const response = await apiService.assignRoleToGroup(customerId, app.id, selectedRole.name, selectedGroupForRole);
      if (response.success) {
        toast.success('Role assigned to group successfully');
        setSelectedGroupForRole('');
        await fetchRoleGroups(selectedRole.name);
      } else {
        toast.error(response.error || 'Failed to assign role to group');
      }
    } catch (error) {
      console.error('Role assignment to group error:', error);
      toast.error('Failed to assign role to group');
    }
  };

  const handleRemoveRoleFromGroup = async (groupName) => {
    const confirmed = await confirm({
      title: 'Remove Role',
      message: `Remove role from group "${groupName}"?`,
      confirmText: 'Remove',
      variant: 'warning',
    });
    if (!confirmed) return;
    try {
      const response = await apiService.removeRoleFromGroup(customerId, app.id, selectedRole.name, groupName);
      if (response.success) {
        toast.success('Role removed from group successfully');
        await fetchRoleGroups(selectedRole.name);
      } else {
        toast.error(response.error || 'Failed to remove role from group');
      }
    } catch (error) {
      console.error('Role removal from group error:', error);
      toast.error('Failed to remove role from group');
    }
  };

  // --- Application Users ---
  const fetchApplicationUsers = async () => {
    setLoadingApplicationUsers(true);
    try {
      const response = await apiService.listApplicationUsers(customerId, app.id);
      if (response.success) {
        setApplicationUsers(response.data.users || []);
      }
    } catch (error) {
      console.error('Failed to fetch application users:', error);
    } finally {
      setLoadingApplicationUsers(false);
    }
  };

  const handleAssignUsersToApp = async () => {
    setSelectedUsersForAssignment([]);
    setUserAssignmentSearch('');
    try {
      const response = await apiService.getAllUsers({ page: 1, pageSize: 1000 });
      if (response.success && response.data) {
        setAvailableUsers(response.data.users || []);
      }
    } catch (error) {
      console.error('Failed to fetch users:', error);
    }
    await fetchApplicationUsers();
    setShowAssignUsersDialog(true);
  };

  const handleAssignUsersSubmit = async () => {
    if (selectedUsersForAssignment.length === 0) {
      toast.error('Please select at least one user');
      return;
    }
    try {
      const response = await apiService.assignUsersToApplication(customerId, app.id, selectedUsersForAssignment);
      if (response.success) {
        toast.success(`${response.data.assigned || selectedUsersForAssignment.length} user(s) assigned successfully`);
        setSelectedUsersForAssignment([]);
        await fetchApplicationUsers();
      } else {
        toast.error(response.error || 'Failed to assign users');
      }
    } catch (error) {
      console.error('User assignment error:', error);
      toast.error('Failed to assign users');
    }
  };

  const handleRemoveUserFromApp = async (username) => {
    const confirmed = await confirm({
      title: 'Remove User',
      message: `Remove "${username}" from this application?`,
      confirmText: 'Remove',
      variant: 'warning',
    });
    if (!confirmed) return;
    try {
      const response = await apiService.removeUserFromApplication(customerId, app.id, username);
      if (response.success) {
        toast.success('User removed from application successfully');
        await fetchApplicationUsers();
      } else {
        toast.error(response.error || 'Failed to remove user');
      }
    } catch (error) {
      console.error('User removal error:', error);
      toast.error('Failed to remove user');
    }
  };

  // --- Application Groups ---
  const fetchApplicationGroups = async () => {
    setLoadingApplicationGroups(true);
    try {
      const response = await apiService.listApplicationGroups(customerId, app.id);
      if (response.success) {
        setApplicationGroups(response.data.groups || []);
      }
    } catch (error) {
      console.error('Failed to fetch application groups:', error);
    } finally {
      setLoadingApplicationGroups(false);
    }
  };

  const handleAssignGroupsToApp = async () => {
    setSelectedGroupsForAssignment([]);
    setGroupAssignmentSearch('');
    try {
      const response = await apiService.getAllGroups({ page: 1, pageSize: 1000 });
      if (response.success && response.data) {
        setAvailableGroups(response.data.groups || []);
      }
    } catch (error) {
      console.error('Failed to fetch groups:', error);
    }
    await fetchApplicationGroups();
    setShowAssignGroupsDialog(true);
  };

  const handleAssignGroupsSubmit = async () => {
    if (selectedGroupsForAssignment.length === 0) {
      toast.error('Please select at least one group');
      return;
    }
    try {
      const response = await apiService.assignGroupsToApplication(customerId, app.id, selectedGroupsForAssignment);
      if (response.success) {
        toast.success(`${response.data.assigned || selectedGroupsForAssignment.length} group(s) assigned successfully`);
        setSelectedGroupsForAssignment([]);
        await fetchApplicationGroups();
      } else {
        toast.error(response.error || 'Failed to assign groups');
      }
    } catch (error) {
      console.error('Group assignment error:', error);
      toast.error('Failed to assign groups');
    }
  };

  const handleRemoveGroupFromApp = async (groupName) => {
    const confirmed = await confirm({
      title: 'Remove Group',
      message: `Remove group "${groupName}" from this application?`,
      confirmText: 'Remove',
      variant: 'warning',
    });
    if (!confirmed) return;
    try {
      const response = await apiService.removeGroupFromApplication(customerId, app.id, groupName);
      if (response.success) {
        toast.success('Group removed from application successfully');
        await fetchApplicationGroups();
      } else {
        toast.error(response.error || 'Failed to remove group');
      }
    } catch (error) {
      console.error('Group removal error:', error);
      toast.error('Failed to remove group');
    }
  };

  const handleCloseAssignUsersDialog = () => {
    setShowAssignUsersDialog(false);
    setUserAssignmentSearch('');
  };

  const handleCloseAssignGroupsDialog = () => {
    setShowAssignGroupsDialog(false);
    setGroupAssignmentSearch('');
  };

  return (
    <motion.div variants={pageVariants} initial="initial" animate="animate">
      {/* Header: Back button + App info + Edit/Delete */}
      <motion.div variants={sectionVariants} style={{ marginBottom: 24 }}>
      <Paper
        elevation={0}
        sx={{
          backgroundColor: '#FFFFFF',
          borderRadius: '8px',
          border: '1px solid #E5E5E5',
          p: 3,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <IconButton
              onClick={onBack}
              sx={{
                color: '#253E66',
                backgroundColor: '#F0F4F8',
                border: '1px solid #E5E5E5',
                '&:hover': { backgroundColor: '#E5E5E5' },
              }}
            >
              <ArrowBackIcon />
            </IconButton>
            {/*
              Generic mark, not the uploaded icon: application branding is shown on the end
              user's application-selection page on the proxy, not in the admin console. The
              icon is still uploaded, replaced and removed from the edit dialog.
            */}
            <Avatar sx={{ backgroundColor: '#27AE60', width: 40, height: 40 }}>
              <AppsIcon sx={{ fontSize: 22 }} />
            </Avatar>
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Typography
                  sx={{
                    fontFamily: 'Bitter, Georgia, serif',
                    fontWeight: 600,
                    fontSize: '1.5rem',
                    color: '#253E66',
                  }}
                >
                  {app.name}
                </Typography>
                <Chip
                  label={app.enabled ? 'Enabled' : 'Disabled'}
                  size="small"
                  sx={{
                    backgroundColor: app.enabled ? '#F0F9F4' : '#FDF2F2',
                    color: app.enabled ? '#27AE60' : '#E74C3C',
                    border: `1px solid ${app.enabled ? '#27AE6020' : '#E74C3C20'}`,
                    fontFamily: 'Metropolis, sans-serif',
                    fontWeight: 500,
                  }}
                />
              </Box>
              {app.description && (
                <Typography sx={{ fontFamily: 'Metropolis, sans-serif', fontSize: '0.875rem', color: '#666666', mt: 0.25 }}>
                  {app.description}
                </Typography>
              )}
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <ReadOnlyButton
              variant="outlined"
              startIcon={<EditIcon />}
              onClick={() => onEdit(app)}
              sx={{
                color: '#253E66',
                borderColor: '#E5E5E5',
                fontFamily: 'Metropolis, sans-serif',
                fontWeight: 600,
                textTransform: 'none',
                borderRadius: '6px',
                '&:hover': { borderColor: '#27AE60', color: '#27AE60' },
              }}
            >
              Edit
            </ReadOnlyButton>
            <ReadOnlyButton
              variant="outlined"
              startIcon={<DeleteIcon />}
              onClick={() => onDelete(app)}
              sx={{
                color: '#E74C3C',
                borderColor: '#E74C3C40',
                fontFamily: 'Metropolis, sans-serif',
                fontWeight: 600,
                textTransform: 'none',
                borderRadius: '6px',
                '&:hover': { borderColor: '#E74C3C', backgroundColor: '#FDF2F2' },
              }}
            >
              Delete
            </ReadOnlyButton>
          </Box>
        </Box>
      </Paper>
      </motion.div>

      {/*
        Connection panel. SAML hands over three values and a metadata document; OIDC hands
        over a realm endpoint set plus per-client values including a live credential, so the
        two are separate panels rather than one panel with holes in it.

        Separate panels, one visual language: both compose their headings and card styles
        from ConnectionPanelSection. The fields differ because the protocols differ; the
        layout must not, or the admin is made to learn two screens for one feature.
      */}
      <motion.div variants={sectionVariants} style={{ marginBottom: 24 }}>
      {isOidc ? (
        /*
          Keyed on the application: swapping `app` in place would otherwise keep the panel
          mounted, and its revealed secret with it, long enough to paint the previous
          application's credential under the new application's heading.
        */
        <OidcConnectionDetails
          key={app.id}
          app={app}
          customerId={customerId}
          copiedKey={copiedKey}
          onCopy={handleCopy}
          endpoints={oidcEndpoints}
          endpointsLoading={loadingEndpoints}
          endpointsError={endpointsError}
        />
      ) : (
        <SamlConnectionPanel
          app={app}
          copiedKey={copiedKey}
          onCopy={handleCopy}
          onDownloadMetadata={handleDownloadMetadata}
          downloadingMetadata={downloadingMetadata}
        />
      )}
      </motion.div>

      {/* Accordion Sections */}
      <motion.div variants={sectionVariants}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {/* 1. Assigned Users Accordion */}
        <Accordion
          sx={{
            backgroundColor: '#FFFFFF',
            border: '1px solid #E5E5E5',
            borderRadius: '8px !important',
            '&:before': { display: 'none' },
            boxShadow: 'none',
            '&.Mui-expanded': { margin: 0 },
          }}
          onChange={(e, expanded) => { if (expanded && applicationUsers === null) fetchApplicationUsers(); }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon sx={{ color: '#999999' }} />}
            sx={{ minHeight: 56, '&.Mui-expanded': { minHeight: 56 }, px: 2.5 }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', pr: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Avatar sx={{ width: 30, height: 30, backgroundColor: '#EBF5FB' }}>
                  <GroupIcon sx={{ fontSize: 16, color: '#4698D3' }} />
                </Avatar>
                <Typography sx={{ fontFamily: 'Metropolis, sans-serif', fontWeight: 600, fontSize: '0.9rem', color: '#253E66' }}>
                  Assigned Users
                </Typography>
                {applicationUsers && (
                  <Chip label={applicationUsers.length} size="small" sx={{ height: 20, fontSize: '0.7rem', fontFamily: 'Metropolis, sans-serif', fontWeight: 600, backgroundColor: '#EBF5FB', color: '#4698D3' }} />
                )}
              </Box>
              <ReadOnlyButton size="small" variant="outlined" startIcon={<PersonAddIcon />}
                onClick={(e) => { e.stopPropagation(); handleAssignUsersToApp(); }}
                sx={{ color: '#4698D3', borderColor: '#4698D340', textTransform: 'none', fontSize: '0.75rem', borderRadius: '6px', '&:hover': { borderColor: '#4698D3', backgroundColor: '#EBF5FB' } }}>
                Assign Users
              </ReadOnlyButton>
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ px: 2.5, pt: 0, pb: 2 }}>
            <Divider sx={{ mb: 1.5, borderColor: '#F0F0F0' }} />
            {loadingApplicationUsers ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}><CircularProgress size={24} sx={{ color: '#4698D3' }} /></Box>
            ) : applicationUsers?.length > 0 ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                {applicationUsers.map((a, idx) => (
                  <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 1.5, backgroundColor: '#F9FBFD', borderRadius: '6px', border: '1px solid #F0F0F0' }}>
                    <Avatar sx={{ width: 28, height: 28, backgroundColor: '#4698D3', fontSize: '0.75rem' }}>
                      {(a.username?.[0] || 'U').toUpperCase()}
                    </Avatar>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography sx={{ fontFamily: 'Metropolis, sans-serif', fontWeight: 500, fontSize: '0.875rem', color: '#253E66' }}>{a.username}</Typography>
                    </Box>
                    <Typography sx={{ fontFamily: 'Metropolis, sans-serif', fontSize: '0.7rem', color: '#BBBBBB', whiteSpace: 'nowrap' }}>
                      {new Date(a.assignedAt).toLocaleDateString()}
                    </Typography>
                    <ReadOnlyIconButton size="small" onClick={() => handleRemoveUserFromApp(a.username)} sx={{ color: '#CCCCCC', '&:hover': { color: '#E74C3C', backgroundColor: '#FDF2F2' } }}>
                      <DeleteIcon sx={{ fontSize: 16 }} />
                    </ReadOnlyIconButton>
                  </Box>
                ))}
              </Box>
            ) : (
              <Box sx={{ py: 2, textAlign: 'center' }}>
                <Typography sx={{ fontFamily: 'Metropolis, sans-serif', fontSize: '0.85rem', color: '#BBBBBB' }}>No users assigned</Typography>
              </Box>
            )}
          </AccordionDetails>
        </Accordion>

        {/* 2. Assigned Groups Accordion */}
        <Accordion
          sx={{
            backgroundColor: '#FFFFFF',
            border: '1px solid #E5E5E5',
            borderRadius: '8px !important',
            '&:before': { display: 'none' },
            boxShadow: 'none',
            '&.Mui-expanded': { margin: 0 },
          }}
          onChange={(e, expanded) => { if (expanded && applicationGroups === null) fetchApplicationGroups(); }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon sx={{ color: '#999999' }} />}
            sx={{ minHeight: 56, '&.Mui-expanded': { minHeight: 56 }, px: 2.5 }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', pr: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Avatar sx={{ width: 30, height: 30, backgroundColor: '#F8E8F2' }}>
                  <GroupsIcon sx={{ fontSize: 16, color: '#D64794' }} />
                </Avatar>
                <Typography sx={{ fontFamily: 'Metropolis, sans-serif', fontWeight: 600, fontSize: '0.9rem', color: '#253E66' }}>
                  Assigned Groups
                </Typography>
                {applicationGroups && (
                  <Chip label={applicationGroups.length} size="small" sx={{ height: 20, fontSize: '0.7rem', fontFamily: 'Metropolis, sans-serif', fontWeight: 600, backgroundColor: '#F8E8F2', color: '#D64794' }} />
                )}
              </Box>
              <ReadOnlyButton size="small" variant="outlined" startIcon={<GroupAddIcon />}
                onClick={(e) => { e.stopPropagation(); handleAssignGroupsToApp(); }}
                sx={{ color: '#D64794', borderColor: '#D6479440', textTransform: 'none', fontSize: '0.75rem', borderRadius: '6px', '&:hover': { borderColor: '#D64794', backgroundColor: '#F8E8F2' } }}>
                Assign Groups
              </ReadOnlyButton>
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ px: 2.5, pt: 0, pb: 2 }}>
            <Divider sx={{ mb: 1.5, borderColor: '#F0F0F0' }} />
            {loadingApplicationGroups ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}><CircularProgress size={24} sx={{ color: '#D64794' }} /></Box>
            ) : applicationGroups?.length > 0 ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                {applicationGroups.map((a, idx) => (
                  <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 1.5, backgroundColor: '#F9FBFD', borderRadius: '6px', border: '1px solid #F0F0F0' }}>
                    <Avatar sx={{ width: 28, height: 28, backgroundColor: '#D64794', fontSize: '0.75rem' }}>
                      <GroupsIcon sx={{ fontSize: 14 }} />
                    </Avatar>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography sx={{ fontFamily: 'Metropolis, sans-serif', fontWeight: 500, fontSize: '0.875rem', color: '#253E66' }}>{a.groupName}</Typography>
                    </Box>
                    <Typography sx={{ fontFamily: 'Metropolis, sans-serif', fontSize: '0.7rem', color: '#BBBBBB', whiteSpace: 'nowrap' }}>
                      {new Date(a.assignedAt).toLocaleDateString()}
                    </Typography>
                    <ReadOnlyIconButton size="small" onClick={() => handleRemoveGroupFromApp(a.groupName)} sx={{ color: '#CCCCCC', '&:hover': { color: '#E74C3C', backgroundColor: '#FDF2F2' } }}>
                      <DeleteIcon sx={{ fontSize: 16 }} />
                    </ReadOnlyIconButton>
                  </Box>
                ))}
              </Box>
            ) : (
              <Box sx={{ py: 2, textAlign: 'center' }}>
                <Typography sx={{ fontFamily: 'Metropolis, sans-serif', fontSize: '0.85rem', color: '#BBBBBB' }}>No groups assigned</Typography>
              </Box>
            )}
          </AccordionDetails>
        </Accordion>

        {/* 3. Roles Accordion */}
        <Accordion
          sx={{
            backgroundColor: '#FFFFFF',
            border: '1px solid #E5E5E5',
            borderRadius: '8px !important',
            '&:before': { display: 'none' },
            boxShadow: 'none',
            '&.Mui-expanded': { margin: 0 },
          }}
          onChange={(e, expanded) => { if (expanded && roles === null) fetchRoles(); }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon sx={{ color: '#999999' }} />}
            sx={{ minHeight: 56, '&.Mui-expanded': { minHeight: 56 }, px: 2.5 }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', pr: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Avatar sx={{ width: 30, height: 30, backgroundColor: '#F5EBF8' }}>
                  <SecurityIcon sx={{ fontSize: 16, color: '#9B59B6' }} />
                </Avatar>
                <Typography sx={{ fontFamily: 'Metropolis, sans-serif', fontWeight: 600, fontSize: '0.9rem', color: '#253E66' }}>
                  Roles
                </Typography>
                {roles && (
                  <Chip label={roles.length} size="small" sx={{ height: 20, fontSize: '0.7rem', fontFamily: 'Metropolis, sans-serif', fontWeight: 600, backgroundColor: '#F5EBF8', color: '#9B59B6' }} />
                )}
              </Box>
              <ReadOnlyButton size="small" variant="outlined" startIcon={<AddIcon />}
                onClick={(e) => { e.stopPropagation(); handleCreateRole(); }}
                sx={{ color: '#9B59B6', borderColor: '#9B59B640', textTransform: 'none', fontSize: '0.75rem', borderRadius: '6px', '&:hover': { borderColor: '#9B59B6', backgroundColor: '#F5EBF8' } }}>
                Create Role
              </ReadOnlyButton>
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ px: 2.5, pt: 0, pb: 2 }}>
            <Divider sx={{ mb: 1.5, borderColor: '#F0F0F0' }} />
            {loadingRoles ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}><CircularProgress size={24} sx={{ color: '#9B59B6' }} /></Box>
            ) : roles?.length > 0 ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                {roles.map((role, idx) => (
                  <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 1.5, backgroundColor: '#F9FBFD', borderRadius: '6px', border: '1px solid #F0F0F0' }}>
                    <Avatar sx={{ width: 28, height: 28, backgroundColor: '#9B59B6', fontSize: '0.75rem' }}>
                      <SecurityIcon sx={{ fontSize: 14 }} />
                    </Avatar>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography sx={{ fontFamily: 'Metropolis, sans-serif', fontWeight: 500, fontSize: '0.875rem', color: '#253E66' }}>{role.name}</Typography>
                      {role.description && (
                        <Typography sx={{ fontFamily: 'Metropolis, sans-serif', fontSize: '0.7rem', color: '#999999' }}>{role.description}</Typography>
                      )}
                    </Box>
                    <Box sx={{ display: 'flex', gap: 0.25 }}>
                      <Tooltip title={isReadOnly ? "Read-only access — you don't have permission to make changes." : "Assign to User"}><span><IconButton size="small" disabled={isReadOnly} onClick={() => handleAssignRole(role)} sx={{ color: '#CCCCCC', '&:hover': { color: '#27AE60', backgroundColor: '#F0F9F4' } }}><PersonAddIcon sx={{ fontSize: 16 }} /></IconButton></span></Tooltip>
                      <Tooltip title={isReadOnly ? "Read-only access — you don't have permission to make changes." : "Assign to Group"}><span><IconButton size="small" disabled={isReadOnly} onClick={() => handleAssignRoleToGroup(role)} sx={{ color: '#CCCCCC', '&:hover': { color: '#D64794', backgroundColor: '#F8E8F2' } }}><GroupAddIcon sx={{ fontSize: 16 }} /></IconButton></span></Tooltip>
                      <Tooltip title={isReadOnly ? "Read-only access — you don't have permission to make changes." : "Delete"}><span><IconButton size="small" disabled={isReadOnly} onClick={() => handleDeleteRole(role.name)} sx={{ color: '#CCCCCC', '&:hover': { color: '#E74C3C', backgroundColor: '#FDF2F2' } }}><DeleteIcon sx={{ fontSize: 16 }} /></IconButton></span></Tooltip>
                    </Box>
                  </Box>
                ))}
              </Box>
            ) : (
              <Box sx={{ py: 2, textAlign: 'center' }}>
                <Typography sx={{ fontFamily: 'Metropolis, sans-serif', fontSize: '0.85rem', color: '#BBBBBB' }}>No roles configured</Typography>
              </Box>
            )}
          </AccordionDetails>
        </Accordion>

        {/* 4. Mappers Accordion */}
        <Accordion
          sx={{
            backgroundColor: '#FFFFFF',
            border: '1px solid #E5E5E5',
            borderRadius: '8px !important',
            '&:before': { display: 'none' },
            boxShadow: 'none',
            '&.Mui-expanded': { margin: 0 },
          }}
          onChange={(e, expanded) => { if (expanded && mappers === null) fetchMappers(); }}
        >
          <AccordionSummary
            slotProps={{ root: { component: 'div' } }}
            expandIcon={<ExpandMoreIcon sx={{ color: '#999999' }} />}
            sx={{ minHeight: 56, '&.Mui-expanded': { minHeight: 56 }, px: 2.5 }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', pr: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Avatar sx={{ width: 30, height: 30, backgroundColor: '#F0F9F4' }}>
                  <AppsIcon sx={{ fontSize: 16, color: '#27AE60' }} />
                </Avatar>
                <Typography sx={{ fontFamily: 'Metropolis, sans-serif', fontWeight: 600, fontSize: '0.9rem', color: '#253E66' }}>
                  Mappers
                </Typography>
                {mappers && (
                  <Chip label={mappers.length} size="small" sx={{ height: 20, fontSize: '0.7rem', fontFamily: 'Metropolis, sans-serif', fontWeight: 600, backgroundColor: '#F0F9F4', color: '#27AE60' }} />
                )}
              </Box>
              <ReadOnlyButton size="small" variant="outlined" startIcon={<AddIcon />}
                onClick={(e) => { e.stopPropagation(); openMapperDialog(); }}
                sx={{ color: '#27AE60', borderColor: '#27AE6040', textTransform: 'none', fontSize: '0.75rem', borderRadius: '6px', '&:hover': { borderColor: '#27AE60', backgroundColor: '#F0F9F4' } }}>
                Add Mapper
              </ReadOnlyButton>
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ px: 2.5, pt: 0, pb: 2 }}>
            <Divider sx={{ mb: 1.5, borderColor: '#F0F0F0' }} />
            {loadingMappers ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}><CircularProgress size={24} sx={{ color: '#27AE60' }} /></Box>
            ) : mappers?.length > 0 ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                {mappers.map((mapper, idx) => (
                  <Accordion
                    key={mapper.id || idx}
                    disableGutters
                    sx={{
                      backgroundColor: '#F9FBFD',
                      borderRadius: '6px !important',
                      border: '1px solid #F0F0F0',
                      '&:before': { display: 'none' },
                      boxShadow: 'none',
                      '&.Mui-expanded': { margin: 0 },
                    }}
                  >
                    <AccordionSummary
                      slotProps={{ root: { component: 'div' } }}
                      expandIcon={<ExpandMoreIcon sx={{ color: '#999999', fontSize: 18 }} />}
                      sx={{ minHeight: 56, '&.Mui-expanded': { minHeight: 56 }, px: 1.5, '& .MuiAccordionSummary-content': { my: 1 } }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%', pr: 1 }}>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography sx={{ fontFamily: 'Metropolis, sans-serif', fontWeight: 500, fontSize: '0.875rem', color: '#253E66' }}>{mapper.name}</Typography>
                          <Typography sx={{ fontFamily: 'Metropolis, sans-serif', fontSize: '0.7rem', color: '#999999' }}>{mapper.protocolMapper}</Typography>
                        </Box>
                        <ReadOnlyIconButton size="small" onClick={(e) => { e.stopPropagation(); handleDeleteMapper(mapper); }} sx={{ color: '#CCCCCC', '&:hover': { color: '#E74C3C', backgroundColor: '#FDF2F2' } }}>
                          <DeleteIcon sx={{ fontSize: 16 }} />
                        </ReadOnlyIconButton>
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails sx={{ px: 1.5, pt: 0, pb: 2 }}>
                      <Divider sx={{ mb: 1.5, borderColor: '#EAEFF5' }} />
                      <MapperDetails mapper={mapper} />
                    </AccordionDetails>
                  </Accordion>
                ))}
              </Box>
            ) : (
              <Box sx={{ py: 2, textAlign: 'center' }}>
                <Typography sx={{ fontFamily: 'Metropolis, sans-serif', fontSize: '0.85rem', color: '#BBBBBB' }}>No mappers configured</Typography>
              </Box>
            )}
          </AccordionDetails>
        </Accordion>
      </Box>
      </motion.div>

      {/* ===== DIALOGS ===== */}

      {/* Mapper Dialog */}
      <Dialog open={showMapperDialog} onClose={() => setShowMapperDialog(false)} maxWidth="sm" fullWidth PaperProps={dialogPaperProps}>
        <DialogTitle sx={{ backgroundColor: '#F0F9F4', borderBottom: '1px solid #E5E5E5', fontFamily: 'Bitter, Georgia, serif', fontWeight: 600, color: '#253E66', display: 'flex', alignItems: 'center', gap: 2, py: 2, px: 3, position: 'relative' }}>
          <Avatar sx={{ backgroundColor: '#27AE60', width: 40, height: 40 }}><AddIcon /></Avatar>
          {isOidc ? 'Create OIDC Mapper' : 'Create SAML Mapper'}
          <IconButton onClick={() => setShowMapperDialog(false)} size="small" sx={{ position: 'absolute', right: 16, top: '50%', transform: 'translateY(-50%)', color: '#666666', backgroundColor: '#FFFFFF', border: '1px solid #E5E5E5' }}><CloseIcon fontSize="small" /></IconButton>
        </DialogTitle>
        <form onSubmit={handleMapperSubmit}>
          <DialogContent sx={{ p: 3, backgroundColor: '#FFFFFF' }}>
            <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: 0.1 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <FormControl fullWidth size="small" sx={selectStyle}>
                <InputLabel>Mapper Type</InputLabel>
                <Select value={mapperData.mapperType} onChange={handleMapperTypeChange} label="Mapper Type">
                  {mapperTypes.map((type) => (
                    <MenuItem key={type.value} value={type.value}>{type.label}</MenuItem>
                  ))}
                </Select>
                {!OIDC_WARNING_TYPES.has(mapperData.mapperType) && (
                  <FormHelperText sx={{ color: '#666666' }}>
                    {getMapperTypeHelperText(mapperData.mapperType, app.protocol)}
                  </FormHelperText>
                )}
              </FormControl>
              {OIDC_WARNING_TYPES.has(mapperData.mapperType) && (
                <Alert
                  severity="warning"
                  sx={{
                    backgroundColor: '#FEF9E7',
                    border: '1px solid #F39C1220',
                    borderRadius: '6px',
                    '& .MuiAlert-icon': { color: '#F39C12' },
                    '& .MuiAlert-message': { fontFamily: 'Metropolis, sans-serif', fontSize: '0.8rem', color: '#253E66' },
                  }}
                >
                  {getMapperTypeHelperText(mapperData.mapperType, app.protocol)}
                </Alert>
              )}
              <TextField label="Mapper Name" value={mapperData.name} onChange={(e) => setMapperData({ ...mapperData, name: e.target.value })} required fullWidth size="small" sx={inputStyle} />
              {mapperData.mapperType === 'user-attribute' && (
                <>
                  <TextField label="SAML Attribute Name" value={mapperData.attributeName} onChange={(e) => setMapperData({ ...mapperData, attributeName: e.target.value })} required fullWidth size="small" sx={inputStyle} />
                  <FormControl fullWidth size="small" sx={selectStyle}>
                    <InputLabel>User Attribute</InputLabel>
                    <Select value={mapperData.userAttribute} onChange={(e) => setMapperData({ ...mapperData, userAttribute: e.target.value })} label="User Attribute">
                      {userAttributes.map((attr) => <MenuItem key={attr.value} value={attr.value}>{attr.label}</MenuItem>)}
                    </Select>
                  </FormControl>
                  <TextField label="Friendly Name (Optional)" value={mapperData.friendlyName} onChange={(e) => setMapperData({ ...mapperData, friendlyName: e.target.value })} fullWidth size="small" sx={inputStyle} />
                </>
              )}
              {(mapperData.mapperType === 'role-list' || mapperData.mapperType === 'group-list') && (
                <>
                  <TextField label="SAML Attribute Name" value={mapperData.attributeName} onChange={(e) => setMapperData({ ...mapperData, attributeName: e.target.value })} fullWidth size="small" placeholder={mapperData.mapperType === 'role-list' ? 'Role' : 'Group'} sx={inputStyle} />
                  <TextField label="Friendly Name (Optional)" value={mapperData.friendlyName} onChange={(e) => setMapperData({ ...mapperData, friendlyName: e.target.value })} fullWidth size="small" sx={inputStyle} />
                </>
              )}
              {mapperData.mapperType === 'group-list' && (
                <>
                  <FormControlLabel control={<Switch checked={mapperData.fullGroupPath} onChange={(e) => setMapperData({ ...mapperData, fullGroupPath: e.target.checked })} sx={{ '& .Mui-checked': { color: '#27AE60' } }} />} label={<Typography sx={{ fontFamily: 'Metropolis, sans-serif', fontSize: '0.875rem', color: '#666666' }}>Full Group Path</Typography>} />
                  <FormControlLabel control={<Switch checked={mapperData.singleGroupAttribute} onChange={(e) => setMapperData({ ...mapperData, singleGroupAttribute: e.target.checked })} sx={{ '& .Mui-checked': { color: '#27AE60' } }} />} label={<Typography sx={{ fontFamily: 'Metropolis, sans-serif', fontSize: '0.875rem', color: '#666666' }}>Single Group Attribute</Typography>} />
                </>
              )}
              {/*
                The SAML branches above are keyed on SAML-only type values, so they can never
                fire for an OIDC client and stay untouched by this. The OIDC fields come from
                the catalogue instead of a hand-written branch per type.
              */}
              {isOidc && oidcFields.map((field) => (
                <OidcMapperField
                  key={field.key}
                  field={field}
                  value={mapperData.oidcConfig[field.key]}
                  onChange={setOidcField}
                />
              ))}
            </Box>
            </motion.div>
          </DialogContent>
          <DialogActions sx={{ p: 3, pt: 2, backgroundColor: '#FFFFFF', borderTop: '1px solid #E5E5E5', gap: 1.5 }}>
            <Button onClick={() => setShowMapperDialog(false)} variant="outlined" sx={{ fontFamily: 'Metropolis, sans-serif', fontWeight: 600, textTransform: 'none', color: '#666666', borderColor: '#E5E5E5', borderRadius: '6px' }}>Cancel</Button>
            <ReadOnlyButton type="submit" variant="contained" sx={{ backgroundColor: '#27AE60', fontFamily: 'Metropolis, sans-serif', fontWeight: 600, textTransform: 'none', borderRadius: '6px', '&:hover': { backgroundColor: '#219A52' } }}>Create Mapper</ReadOnlyButton>
          </DialogActions>
        </form>
      </Dialog>

      {/* Role Dialog */}
      <Dialog open={showRoleDialog} onClose={() => setShowRoleDialog(false)} maxWidth="sm" fullWidth PaperProps={dialogPaperProps}>
        <DialogTitle sx={{ backgroundColor: '#F5EBF8', borderBottom: '1px solid #E5E5E5', fontFamily: 'Bitter, Georgia, serif', fontWeight: 600, color: '#253E66', display: 'flex', alignItems: 'center', gap: 2, py: 2, px: 3, position: 'relative' }}>
          <Avatar sx={{ backgroundColor: '#9B59B6', width: 40, height: 40 }}><SecurityIcon /></Avatar>
          Create Role
          <IconButton onClick={() => setShowRoleDialog(false)} size="small" sx={{ position: 'absolute', right: 16, top: '50%', transform: 'translateY(-50%)', color: '#666666', backgroundColor: '#FFFFFF', border: '1px solid #E5E5E5' }}><CloseIcon fontSize="small" /></IconButton>
        </DialogTitle>
        <form onSubmit={handleRoleSubmit}>
          <DialogContent sx={{ p: 3, backgroundColor: '#FFFFFF' }}>
            <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: 0.1 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField label="Role Name" value={roleData.roleName} onChange={(e) => setRoleData({ ...roleData, roleName: e.target.value })} required fullWidth size="small" helperText="e.g., 'admin', 'editor', 'viewer'" sx={inputStyle} />
              <TextField label="Description (Optional)" value={roleData.description} onChange={(e) => setRoleData({ ...roleData, description: e.target.value })} fullWidth size="small" multiline rows={2} sx={inputStyle} />
            </Box>
            </motion.div>
          </DialogContent>
          <DialogActions sx={{ p: 3, pt: 2, backgroundColor: '#FFFFFF', borderTop: '1px solid #E5E5E5', gap: 1.5 }}>
            <Button onClick={() => setShowRoleDialog(false)} variant="outlined" sx={{ fontFamily: 'Metropolis, sans-serif', fontWeight: 600, textTransform: 'none', color: '#666666', borderColor: '#E5E5E5', borderRadius: '6px' }}>Cancel</Button>
            <ReadOnlyButton type="submit" variant="contained" sx={{ backgroundColor: '#9B59B6', fontFamily: 'Metropolis, sans-serif', fontWeight: 600, textTransform: 'none', borderRadius: '6px', '&:hover': { backgroundColor: '#8E44AD' } }}>Create Role</ReadOnlyButton>
          </DialogActions>
        </form>
      </Dialog>

      {/* Assign Role to User Dialog */}
      <Dialog open={showAssignRoleDialog} onClose={() => setShowAssignRoleDialog(false)} maxWidth="sm" fullWidth PaperProps={dialogPaperProps}>
        <DialogTitle sx={{ backgroundColor: '#F0F9F4', borderBottom: '1px solid #E5E5E5', fontFamily: 'Bitter, Georgia, serif', fontWeight: 600, color: '#253E66', display: 'flex', alignItems: 'center', gap: 2, py: 2, px: 3, position: 'relative' }}>
          <Avatar sx={{ backgroundColor: '#27AE60', width: 40, height: 40 }}><PersonAddIcon /></Avatar>
          Assign Role: {selectedRole?.name}
          <IconButton onClick={() => setShowAssignRoleDialog(false)} size="small" sx={{ position: 'absolute', right: 16, top: '50%', transform: 'translateY(-50%)', color: '#666666', backgroundColor: '#FFFFFF', border: '1px solid #E5E5E5' }}><CloseIcon fontSize="small" /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 3, backgroundColor: '#FFFFFF' }}>
          <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: 0.1 }}>
          <Box sx={{ mb: 3 }}>
            <Typography sx={{ fontFamily: 'Metropolis, sans-serif', fontWeight: 600, fontSize: '0.9rem', color: '#253E66', mb: 2 }}>Assign to User</Typography>
            {availableUsers.length === 0 ? (
              <Alert severity="warning" sx={{ backgroundColor: '#FEF9E7', border: '1px solid #F39C1220' }}>No users assigned to this application</Alert>
            ) : (
              <Box sx={{ display: 'flex', gap: 1 }}>
                <FormControl fullWidth size="small" sx={selectStyle}>
                  <InputLabel>Select User</InputLabel>
                  <Select value={selectedUserForRole} onChange={(e) => setSelectedUserForRole(e.target.value)} label="Select User">
                    {availableUsers.filter(u => !roleUsers[selectedRole?.name]?.some(ru => ru.username === u.username)).map((u) => <MenuItem key={u.username} value={u.username}>{u.username}</MenuItem>)}
                  </Select>
                </FormControl>
                <ReadOnlyButton variant="contained" onClick={handleAssignRoleToUser} disabled={!selectedUserForRole} sx={{ backgroundColor: '#27AE60', minWidth: 100, '&:hover': { backgroundColor: '#219A52' } }}>Assign</ReadOnlyButton>
              </Box>
            )}
          </Box>
          <Divider sx={{ mb: 3, borderColor: '#E5E5E5' }} />
          <Typography sx={{ fontFamily: 'Metropolis, sans-serif', fontWeight: 600, fontSize: '0.9rem', color: '#253E66', mb: 2 }}>Users with this Role</Typography>
          {roleUsers[selectedRole?.name]?.length > 0 ? (
            <List dense>{roleUsers[selectedRole?.name].map((u) => (
              <ListItem key={u.id} sx={{ backgroundColor: '#F9FBFD', borderRadius: '4px', mb: 0.5, border: '1px solid #E5E5E5' }}>
                <ListItemText primary={<Typography sx={{ fontFamily: 'Metropolis, sans-serif', fontWeight: 500, color: '#253E66' }}>{u.username}</Typography>} />
                <ReadOnlyIconButton size="small" onClick={() => handleRemoveRoleFromUser(u.username)} sx={{ color: '#E74C3C' }}><DeleteIcon fontSize="small" /></ReadOnlyIconButton>
              </ListItem>
            ))}</List>
          ) : <Typography sx={{ fontFamily: 'Metropolis, sans-serif', fontSize: '0.875rem', color: '#999999', fontStyle: 'italic' }}>No users assigned</Typography>}
          </motion.div>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 0, backgroundColor: '#FFFFFF' }}>
          <Button onClick={() => setShowAssignRoleDialog(false)} variant="outlined" sx={{ fontFamily: 'Metropolis, sans-serif', fontWeight: 600, textTransform: 'none', color: '#666666', borderColor: '#E5E5E5', borderRadius: '6px' }}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Assign Users to Application Dialog */}
      <Dialog open={showAssignUsersDialog} onClose={handleCloseAssignUsersDialog} maxWidth="sm" fullWidth PaperProps={dialogPaperProps}>
        <DialogTitle sx={{ backgroundColor: '#EBF5FB', borderBottom: '1px solid #E5E5E5', fontFamily: 'Bitter, Georgia, serif', fontWeight: 600, color: '#253E66', display: 'flex', alignItems: 'center', gap: 2, py: 2, px: 3, position: 'relative' }}>
          <Avatar sx={{ backgroundColor: '#4698D3', width: 40, height: 40 }}><GroupIcon /></Avatar>
          <Box><Typography sx={{ fontFamily: 'Bitter, Georgia, serif', fontWeight: 600, color: '#253E66' }}>Manage Application Users</Typography><Typography sx={{ fontFamily: 'Metropolis, sans-serif', fontSize: '0.75rem', color: '#999999' }}>{app.name}</Typography></Box>
          <IconButton onClick={handleCloseAssignUsersDialog} size="small" sx={{ position: 'absolute', right: 16, top: '50%', transform: 'translateY(-50%)', color: '#666666', backgroundColor: '#FFFFFF', border: '1px solid #E5E5E5' }}><CloseIcon fontSize="small" /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 3, backgroundColor: '#FFFFFF' }}>
          <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: 0.1 }}>
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
              <PersonAddIcon sx={{ color: '#4698D3', fontSize: 22 }} />
              <Typography sx={{ fontFamily: 'Metropolis, sans-serif', fontWeight: 600, fontSize: '0.9rem', color: '#253E66' }}>
                Assign Users
              </Typography>
              {selectedUsersForAssignment.length > 0 && (
                <Chip
                  label={selectedUsersForAssignment.length}
                  size="small"
                  sx={{ backgroundColor: '#EBF5FB', color: '#4698D3', fontFamily: 'Metropolis, sans-serif', fontWeight: 600, height: 20, fontSize: '0.75rem' }}
                />
              )}
            </Box>

            <TextField
              fullWidth
              size="small"
              placeholder="Search users..."
              value={userAssignmentSearch}
              onChange={(e) => setUserAssignmentSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: '#999999', fontSize: 20 }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                mb: 1,
                '& .MuiOutlinedInput-root': {
                  fontFamily: 'Metropolis, sans-serif',
                  backgroundColor: '#FFFFFF',
                  '& fieldset': { borderColor: '#E5E5E5' },
                  '&:hover fieldset': { borderColor: '#4698D3' },
                  '&.Mui-focused fieldset': { borderColor: '#4698D3' },
                },
              }}
            />

            <Paper
              elevation={0}
              sx={{
                backgroundColor: '#F9FBFD',
                border: '1px solid #E5E5E5',
                borderRadius: '6px',
                maxHeight: 200,
                overflowY: 'auto',
                mb: 1,
              }}
            >
              {filteredAvailableUsers.length > 0 && (
                <Box sx={{ borderBottom: '1px solid #E5E5E5', px: 1, py: 0.5, backgroundColor: '#FFFFFF' }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={isAllUsersSelected}
                        indeterminate={isUsersIndeterminate}
                        onChange={handleSelectAllUsers}
                        sx={{ color: '#E5E5E5', '&.Mui-checked': { color: '#4698D3' }, '&.MuiCheckbox-indeterminate': { color: '#4698D3' } }}
                      />
                    }
                    label={<Typography sx={{ fontFamily: 'Metropolis, sans-serif', fontSize: '0.85rem', fontWeight: 600, color: '#666666' }}>Select All</Typography>}
                  />
                </Box>
              )}
              {filteredAvailableUsers.length === 0 ? (
                <Box sx={{ p: 2, textAlign: 'center' }}>
                  <Typography sx={{ fontFamily: 'Metropolis, sans-serif', fontSize: '0.85rem', color: '#999999', fontStyle: 'italic' }}>
                    {userDirectory.loading
                      ? 'Searching users...'
                      : userAssignmentSearch ? 'No users match your search' : 'No users available'}
                  </Typography>
                </Box>
              ) : (
                filteredAvailableUsers.map((user) => (
                  <Box
                    key={user.username}
                    onClick={() => handleToggleUserAssignment(user.username)}
                    sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1, py: 0.5, cursor: 'pointer', '&:hover': { backgroundColor: '#FFFFFF' }, borderBottom: '1px solid #F0F0F0' }}
                  >
                    <Checkbox
                      checked={selectedUsersForAssignment.includes(user.username)}
                      sx={{ color: '#E5E5E5', '&.Mui-checked': { color: '#4698D3' } }}
                    />
                    <Avatar sx={{ width: 28, height: 28, backgroundColor: '#4698D3', fontSize: '0.75rem' }}>
                      {(user.firstName?.[0] || user.username?.[0] || 'U').toUpperCase()}
                    </Avatar>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography sx={{ fontFamily: 'Metropolis, sans-serif', fontSize: '0.9rem', fontWeight: 500, color: '#253E66' }}>
                        {[user.firstName, user.lastName].filter(Boolean).join(' ') || user.username}
                      </Typography>
                      <Typography sx={{ fontFamily: 'Metropolis, sans-serif', fontSize: '0.75rem', color: '#666666', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {user.email || user.username}
                      </Typography>
                    </Box>
                  </Box>
                ))
              )}
            </Paper>

            {selectedUsersForAssignment.length > 0 && (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
                {selectedUsersForAssignment.map((username) => (
                  <Chip
                    key={username}
                    label={username}
                    size="small"
                    onDelete={() => handleToggleUserAssignment(username)}
                    sx={{
                      backgroundColor: '#EBF5FB',
                      color: '#4698D3',
                      border: '1px solid #4698D330',
                      fontFamily: 'Metropolis, sans-serif',
                      fontWeight: 500,
                      '& .MuiChip-deleteIcon': { color: '#4698D3', '&:hover': { color: '#3580B8' } },
                    }}
                  />
                ))}
              </Box>
            )}

            <ReadOnlyButton
              variant="contained"
              onClick={handleAssignUsersSubmit}
              disabled={selectedUsersForAssignment.length === 0}
              fullWidth
              startIcon={<PersonAddIcon />}
              sx={{ backgroundColor: '#4698D3', fontFamily: 'Metropolis, sans-serif', fontWeight: 600, textTransform: 'none', borderRadius: '6px', '&:hover': { backgroundColor: '#3580B8' } }}
            >
              Assign Selected Users
            </ReadOnlyButton>
          </Box>

          <Divider sx={{ mb: 3, borderColor: '#E5E5E5' }} />

          <Typography sx={{ fontFamily: 'Metropolis, sans-serif', fontWeight: 600, fontSize: '0.9rem', color: '#253E66', mb: 2 }}>
            Currently Assigned ({applicationUsers?.length || 0})
          </Typography>
          {loadingApplicationUsers ? <CircularProgress size={24} /> : applicationUsers?.length > 0 ? (
            <List dense sx={{ maxHeight: 200, overflow: 'auto' }}>{applicationUsers.map((a) => (
              <ListItem key={a.id} sx={{ backgroundColor: '#F9FBFD', borderRadius: '4px', mb: 0.5, border: '1px solid #E5E5E5' }}>
                <Avatar sx={{ width: 24, height: 24, backgroundColor: '#4698D3', fontSize: '0.7rem', mr: 1 }}>
                  {(a.username?.[0] || 'U').toUpperCase()}
                </Avatar>
                <ListItemText primary={<Typography sx={{ fontFamily: 'Metropolis, sans-serif', fontWeight: 500, color: '#253E66' }}>{a.username}</Typography>} />
                <ReadOnlyIconButton size="small" onClick={() => handleRemoveUserFromApp(a.username)} sx={{ color: '#E74C3C' }}><DeleteIcon fontSize="small" /></ReadOnlyIconButton>
              </ListItem>
            ))}</List>
          ) : <Typography sx={{ fontFamily: 'Metropolis, sans-serif', fontSize: '0.875rem', color: '#999999', fontStyle: 'italic' }}>No users assigned</Typography>}
          </motion.div>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 0, backgroundColor: '#FFFFFF' }}>
          <Button onClick={handleCloseAssignUsersDialog} variant="outlined" sx={{ fontFamily: 'Metropolis, sans-serif', fontWeight: 600, textTransform: 'none', color: '#666666', borderColor: '#E5E5E5', borderRadius: '6px' }}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Assign Groups to Application Dialog */}
      <Dialog open={showAssignGroupsDialog} onClose={handleCloseAssignGroupsDialog} maxWidth="sm" fullWidth PaperProps={dialogPaperProps}>
        <DialogTitle sx={{ backgroundColor: '#F8E8F2', borderBottom: '1px solid #E5E5E5', fontFamily: 'Bitter, Georgia, serif', fontWeight: 600, color: '#253E66', display: 'flex', alignItems: 'center', gap: 2, py: 2, px: 3, position: 'relative' }}>
          <Avatar sx={{ backgroundColor: '#D64794', width: 40, height: 40 }}><GroupsIcon /></Avatar>
          <Box><Typography sx={{ fontFamily: 'Bitter, Georgia, serif', fontWeight: 600, color: '#253E66' }}>Manage Application Groups</Typography><Typography sx={{ fontFamily: 'Metropolis, sans-serif', fontSize: '0.75rem', color: '#999999' }}>{app.name}</Typography></Box>
          <IconButton onClick={handleCloseAssignGroupsDialog} size="small" sx={{ position: 'absolute', right: 16, top: '50%', transform: 'translateY(-50%)', color: '#666666', backgroundColor: '#FFFFFF', border: '1px solid #E5E5E5' }}><CloseIcon fontSize="small" /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 3, backgroundColor: '#FFFFFF' }}>
          <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: 0.1 }}>
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
              <GroupAddIcon sx={{ color: '#D64794', fontSize: 22 }} />
              <Typography sx={{ fontFamily: 'Metropolis, sans-serif', fontWeight: 600, fontSize: '0.9rem', color: '#253E66' }}>
                Assign Groups
              </Typography>
              {selectedGroupsForAssignment.length > 0 && (
                <Chip
                  label={selectedGroupsForAssignment.length}
                  size="small"
                  sx={{ backgroundColor: '#F8E8F2', color: '#D64794', fontFamily: 'Metropolis, sans-serif', fontWeight: 600, height: 20, fontSize: '0.75rem' }}
                />
              )}
            </Box>

            <TextField
              fullWidth
              size="small"
              placeholder="Search groups..."
              value={groupAssignmentSearch}
              onChange={(e) => setGroupAssignmentSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: '#999999', fontSize: 20 }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                mb: 1,
                '& .MuiOutlinedInput-root': {
                  fontFamily: 'Metropolis, sans-serif',
                  backgroundColor: '#FFFFFF',
                  '& fieldset': { borderColor: '#E5E5E5' },
                  '&:hover fieldset': { borderColor: '#D64794' },
                  '&.Mui-focused fieldset': { borderColor: '#D64794' },
                },
              }}
            />

            <Paper
              elevation={0}
              sx={{
                backgroundColor: '#F9FBFD',
                border: '1px solid #E5E5E5',
                borderRadius: '6px',
                maxHeight: 200,
                overflowY: 'auto',
                mb: 1,
              }}
            >
              {filteredAvailableGroups.length > 0 && (
                <Box sx={{ borderBottom: '1px solid #E5E5E5', px: 1, py: 0.5, backgroundColor: '#FFFFFF' }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={isAllGroupsSelected}
                        indeterminate={isGroupsIndeterminate}
                        onChange={handleSelectAllGroups}
                        sx={{ color: '#E5E5E5', '&.Mui-checked': { color: '#D64794' }, '&.MuiCheckbox-indeterminate': { color: '#D64794' } }}
                      />
                    }
                    label={<Typography sx={{ fontFamily: 'Metropolis, sans-serif', fontSize: '0.85rem', fontWeight: 600, color: '#666666' }}>Select All</Typography>}
                  />
                </Box>
              )}
              {filteredAvailableGroups.length === 0 ? (
                <Box sx={{ p: 2, textAlign: 'center' }}>
                  <Typography sx={{ fontFamily: 'Metropolis, sans-serif', fontSize: '0.85rem', color: '#999999', fontStyle: 'italic' }}>
                    {groupDirectory.loading
                      ? 'Searching groups...'
                      : groupAssignmentSearch ? 'No groups match your search' : 'No groups available'}
                  </Typography>
                </Box>
              ) : (
                filteredAvailableGroups.map((group) => (
                  <Box
                    key={group.name || group.id}
                    onClick={() => handleToggleGroupAssignment(group.name)}
                    sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1, py: 0.5, cursor: 'pointer', '&:hover': { backgroundColor: '#FFFFFF' }, borderBottom: '1px solid #F0F0F0' }}
                  >
                    <Checkbox
                      checked={selectedGroupsForAssignment.includes(group.name)}
                      sx={{ color: '#E5E5E5', '&.Mui-checked': { color: '#D64794' } }}
                    />
                    <Avatar sx={{ width: 28, height: 28, backgroundColor: '#D64794', fontSize: '0.75rem' }}>
                      <GroupsIcon sx={{ fontSize: 16 }} />
                    </Avatar>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography sx={{ fontFamily: 'Metropolis, sans-serif', fontSize: '0.9rem', fontWeight: 500, color: '#253E66' }}>
                        {group.name}
                      </Typography>
                      {group.description && (
                        <Typography sx={{ fontFamily: 'Metropolis, sans-serif', fontSize: '0.75rem', color: '#666666', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {group.description}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                ))
              )}
            </Paper>

            {selectedGroupsForAssignment.length > 0 && (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
                {selectedGroupsForAssignment.map((groupName) => (
                  <Chip
                    key={groupName}
                    label={groupName}
                    size="small"
                    onDelete={() => handleToggleGroupAssignment(groupName)}
                    sx={{
                      backgroundColor: '#F8E8F2',
                      color: '#D64794',
                      border: '1px solid #D6479430',
                      fontFamily: 'Metropolis, sans-serif',
                      fontWeight: 500,
                      '& .MuiChip-deleteIcon': { color: '#D64794', '&:hover': { color: '#C13584' } },
                    }}
                  />
                ))}
              </Box>
            )}

            <ReadOnlyButton
              variant="contained"
              onClick={handleAssignGroupsSubmit}
              disabled={selectedGroupsForAssignment.length === 0}
              fullWidth
              startIcon={<GroupAddIcon />}
              sx={{ backgroundColor: '#D64794', fontFamily: 'Metropolis, sans-serif', fontWeight: 600, textTransform: 'none', borderRadius: '6px', '&:hover': { backgroundColor: '#C13584' } }}
            >
              Assign Selected Groups
            </ReadOnlyButton>
          </Box>

          <Divider sx={{ mb: 3, borderColor: '#E5E5E5' }} />

          <Typography sx={{ fontFamily: 'Metropolis, sans-serif', fontWeight: 600, fontSize: '0.9rem', color: '#253E66', mb: 2 }}>
            Currently Assigned ({applicationGroups?.length || 0})
          </Typography>
          {loadingApplicationGroups ? <CircularProgress size={24} /> : applicationGroups?.length > 0 ? (
            <List dense sx={{ maxHeight: 200, overflow: 'auto' }}>{applicationGroups.map((a) => (
              <ListItem key={a.id} sx={{ backgroundColor: '#F9FBFD', borderRadius: '4px', mb: 0.5, border: '1px solid #E5E5E5' }}>
                <Avatar sx={{ width: 24, height: 24, backgroundColor: '#D64794', fontSize: '0.7rem', mr: 1 }}>
                  <GroupsIcon sx={{ fontSize: 14 }} />
                </Avatar>
                <ListItemText primary={<Typography sx={{ fontFamily: 'Metropolis, sans-serif', fontWeight: 500, color: '#253E66' }}>{a.groupName}</Typography>} />
                <ReadOnlyIconButton size="small" onClick={() => handleRemoveGroupFromApp(a.groupName)} sx={{ color: '#E74C3C' }}><DeleteIcon fontSize="small" /></ReadOnlyIconButton>
              </ListItem>
            ))}</List>
          ) : <Typography sx={{ fontFamily: 'Metropolis, sans-serif', fontSize: '0.875rem', color: '#999999', fontStyle: 'italic' }}>No groups assigned</Typography>}
          </motion.div>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 0, backgroundColor: '#FFFFFF' }}>
          <Button onClick={handleCloseAssignGroupsDialog} variant="outlined" sx={{ fontFamily: 'Metropolis, sans-serif', fontWeight: 600, textTransform: 'none', color: '#666666', borderColor: '#E5E5E5', borderRadius: '6px' }}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Assign Role to Group Dialog */}
      <Dialog open={showAssignRoleToGroupDialog} onClose={() => setShowAssignRoleToGroupDialog(false)} maxWidth="sm" fullWidth PaperProps={dialogPaperProps}>
        <DialogTitle sx={{ backgroundColor: '#F8E8F2', borderBottom: '1px solid #E5E5E5', fontFamily: 'Bitter, Georgia, serif', fontWeight: 600, color: '#253E66', display: 'flex', alignItems: 'center', gap: 2, py: 2, px: 3, position: 'relative' }}>
          <Avatar sx={{ backgroundColor: '#D64794', width: 40, height: 40 }}><GroupAddIcon /></Avatar>
          Assign Role to Groups: {selectedRole?.name}
          <IconButton onClick={() => setShowAssignRoleToGroupDialog(false)} size="small" sx={{ position: 'absolute', right: 16, top: '50%', transform: 'translateY(-50%)', color: '#666666', backgroundColor: '#FFFFFF', border: '1px solid #E5E5E5' }}><CloseIcon fontSize="small" /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 3, backgroundColor: '#FFFFFF' }}>
          <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: 0.1 }}>
          <Box sx={{ mb: 3 }}>
            <Typography sx={{ fontFamily: 'Metropolis, sans-serif', fontWeight: 600, fontSize: '0.9rem', color: '#253E66', mb: 2 }}>Assign to Group</Typography>
            {availableGroups.length === 0 ? (
              <Alert severity="warning" sx={{ backgroundColor: '#FEF9E7', border: '1px solid #F39C1220' }}>No groups assigned to this application</Alert>
            ) : (
              <Box sx={{ display: 'flex', gap: 1 }}>
                <FormControl fullWidth size="small" sx={selectStyle}>
                  <InputLabel>Select Group</InputLabel>
                  <Select value={selectedGroupForRole} onChange={(e) => setSelectedGroupForRole(e.target.value)} label="Select Group">
                    {availableGroups.filter(g => !roleGroups[selectedRole?.name]?.some(rg => rg.name === g.name)).map((g) => <MenuItem key={g.name} value={g.name}>{g.name}</MenuItem>)}
                  </Select>
                </FormControl>
                <ReadOnlyButton variant="contained" onClick={handleAssignRoleToGroupSubmit} disabled={!selectedGroupForRole} sx={{ backgroundColor: '#D64794', minWidth: 100, '&:hover': { backgroundColor: '#C13584' } }}>Assign</ReadOnlyButton>
              </Box>
            )}
          </Box>
          <Divider sx={{ mb: 3, borderColor: '#E5E5E5' }} />
          <Typography sx={{ fontFamily: 'Metropolis, sans-serif', fontWeight: 600, fontSize: '0.9rem', color: '#253E66', mb: 2 }}>Groups with this Role</Typography>
          {roleGroups[selectedRole?.name]?.length > 0 ? (
            <List dense>{roleGroups[selectedRole?.name].map((g) => (
              <ListItem key={g.id || g.name} sx={{ backgroundColor: '#F9FBFD', borderRadius: '4px', mb: 0.5, border: '1px solid #E5E5E5' }}>
                <ListItemText primary={<Typography sx={{ fontFamily: 'Metropolis, sans-serif', fontWeight: 500, color: '#253E66' }}>{g.name}</Typography>} />
                <ReadOnlyIconButton size="small" onClick={() => handleRemoveRoleFromGroup(g.name)} sx={{ color: '#E74C3C' }}><DeleteIcon fontSize="small" /></ReadOnlyIconButton>
              </ListItem>
            ))}</List>
          ) : <Typography sx={{ fontFamily: 'Metropolis, sans-serif', fontSize: '0.875rem', color: '#999999', fontStyle: 'italic' }}>No groups assigned</Typography>}
          </motion.div>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 0, backgroundColor: '#FFFFFF' }}>
          <Button onClick={() => setShowAssignRoleToGroupDialog(false)} variant="outlined" sx={{ fontFamily: 'Metropolis, sans-serif', fontWeight: 600, textTransform: 'none', color: '#666666', borderColor: '#E5E5E5', borderRadius: '6px' }}>Close</Button>
        </DialogActions>
      </Dialog>
    </motion.div>
  );
};

export default CustomApplicationDetailView;
